import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { FaSpinner, FaCheck, FaTimes, FaEdit, FaSave, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../context/AuthContext';
import RoadmapPdfExport from '../reports/RoadmapPDFExport';
import RoadmapGanttChart from '../reports/RoadmapGanttChart';

const ImplementationRoadmap = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    priority: 'all',
    organizationId: '',
    assessmentId: 'all'
  });
  const [organizationName, setOrganizationName] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [assessments, setAssessments] = useState([]);

  useEffect(() => {
    fetchOrganizations();
  }, []);
  
  useEffect(() => {
    if (filters.organizationId) {
      // Clear previous assessments when organization changes
      setAssessments([]);
      fetchAssessments(filters.organizationId);
      fetchRecommendations(filters.organizationId, filters.assessmentId);
      
      // Update organization name when organization changes
      const selectedOrg = organizations.find(org => org._id === filters.organizationId);
      if (selectedOrg) {
        setOrganizationName(selectedOrg.name);
      }
    }
  }, [filters.organizationId]);
  
  useEffect(() => {
    if (filters.organizationId && filters.assessmentId) {
      fetchRecommendations(filters.organizationId, filters.assessmentId);
    }
  }, [filters.assessmentId]);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/organizations/user', {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data && response.data.length > 0) {
        setOrganizations(response.data);
        // Set default organization to the first one
        const defaultOrg = response.data[0];
        setFilters(prev => ({ ...prev, organizationId: defaultOrg._id }));
        setOrganizationName(defaultOrg.name);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations. Please try again.');
    }
  };
  
  const fetchAssessments = async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      
      // First try to get all assessments for the organization
      console.log(`Fetching assessments for organization: ${orgId}`);
      const response = await axios.get(`/api/assessments?organizationId=${orgId}`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data && response.data.length > 0) {
        // Filter for completed assessments on the client side
        const completedAssessments = response.data.filter(assessment => 
          assessment.status === 'completed'
        );
        
        console.log(`Found ${completedAssessments.length} completed assessments out of ${response.data.length} total`);
        completedAssessments.forEach(assessment => {
          console.log(`- ${assessment.title} (ID: ${assessment._id}, Status: ${assessment.status})`);
        });
        
        setAssessments(completedAssessments);
      } else {
        console.log('No assessments found for this organization');
        setAssessments([]);
      }
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments. Please try again.');
    }
  };

  const fetchRecommendations = async (orgId, assessmentId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let url = '/api/recommendations/roadmap';
      
      // Add query parameters if provided
      const params = new URLSearchParams();
      if (orgId) params.append('organizationId', orgId);
      if (assessmentId && assessmentId !== 'all') params.append('assessmentId', assessmentId);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log(`Fetching recommendations with URL: ${url}`);
      const response = await axios.get(url, {
        headers: { 'x-auth-token': token }
      });
      
      setRecommendations(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to load implementation roadmap. Please try again.');
      setLoading(false);
    }
  };

  const handleUpdateRecommendation = async (docId, recommendationIndex, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/recommendations/${docId}`, {
        recommendationIndex,
        updates
      }, {
        headers: { 'x-auth-token': token }
      });
      
      // Update local state
      setRecommendations(prevRecommendations => {
        return prevRecommendations.map(doc => {
          if (doc._id === docId) {
            const updatedRecommendations = [...doc.recommendations];
            updatedRecommendations[recommendationIndex] = {
              ...updatedRecommendations[recommendationIndex],
              ...updates
            };
            return {
              ...doc,
              recommendations: updatedRecommendations
            };
          }
          return doc;
        });
      });
      
      setEditingItem(null);
    } catch (err) {
      console.error('Error updating recommendation:', err);
      alert('Failed to update recommendation. Please try again.');
    }
  };

  const handleEditClick = (docId, recommendationIndex) => {
    setEditingItem({ docId, index: recommendationIndex });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value
    });
  };

  const filteredRecommendations = recommendations.filter(doc => {
    if (filters.category !== 'all' && doc.category !== filters.category) {
      return false;
    }
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Not Started':
        return '#DC3545'; // Red
      case 'In Progress':
        return '#F4D03F'; // Yellow
      case 'Completed':
        return '#198754'; // Green
      default:
        return '#6C757D'; // Gray
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return '#DC3545'; // Red
      case 'High':
        return '#E67E22'; // Orange
      case 'Medium':
        return '#F4D03F'; // Yellow
      case 'Low':
        return '#198754'; // Green
      default:
        return '#6C757D'; // Gray
    }
  };

  // Get unique categories for filter dropdown
  const categories = ['all', ...new Set(recommendations.map(doc => doc.category))];

  return (
    <div style={styles.container}>
      <div style={styles.titleContainer}>
        <div>
          <h1 style={styles.title}>{t('Implementation Roadmap')}</h1>
          <p style={styles.description}>
            {t('Track and manage recommendations selected from AI analysis to improve your resilience maturity.')}
          </p>
        </div>
        <div style={styles.exportButtonsContainer}>
          <RoadmapPdfExport 
            recommendations={filteredRecommendations} 
            title="Implementation Roadmap" 
            organizationName={organizationName} 
          />
          <RoadmapGanttChart 
            recommendations={filteredRecommendations}
            organizationName={organizationName}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{t('Organization')}:</label>
          <select 
            value={filters.organizationId} 
            onChange={(e) => handleFilterChange('organizationId', e.target.value)}
            style={styles.filterSelect}
            required
          >
            {organizations.length === 0 ? (
              <option value="">{t('Loading...')}</option>
            ) : (
              organizations.map((org) => (
                <option key={org._id} value={org._id}>{org.name}</option>
              ))
            )}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{t('Assessment')}:</label>
          <select 
            value={filters.assessmentId} 
            onChange={(e) => handleFilterChange('assessmentId', e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{t('All Assessments')}</option>
            {assessments && assessments.length > 0 ? (
              assessments.map((assessment) => {
                // Debug what fields are available
                console.log('Assessment object:', assessment);
                // Try to get a display name from various possible fields
                const displayName = assessment.title || 
                                   (assessment.industryType ? `${assessment.industryType} Assessment` : null) || 
                                   `Assessment ${assessment._id.substring(0, 8)}`;
                return (
                  <option key={assessment._id} value={assessment._id}>
                    {displayName}
                  </option>
                );
              })
            ) : (
              <option value="" disabled>{t('No completed assessments found')}</option>
            )}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{t('Category')}:</label>
          <select 
            value={filters.category} 
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{t('All Categories')}</option>
            {categories.filter(cat => cat !== 'all').map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{t('Status')}:</label>
          <select 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{t('All Statuses')}</option>
            <option value="Not Started">{t('Not Started')}</option>
            <option value="In Progress">{t('In Progress')}</option>
            <option value="Completed">{t('Completed')}</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{t('Priority')}:</label>
          <select 
            value={filters.priority} 
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{t('All Priorities')}</option>
            <option value="Critical">{t('Critical')}</option>
            <option value="High">{t('High')}</option>
            <option value="Medium">{t('Medium')}</option>
            <option value="Low">{t('Low')}</option>
          </select>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.spinner} />
          <p>{t('Loading recommendations...')}</p>
        </div>
      ) : error ? (
        <div style={styles.errorContainer}>
          <FaExclamationTriangle style={styles.errorIcon} />
          <p>{error}</p>
          <button style={styles.retryButton} onClick={fetchRecommendations}>
            {t('Retry')}
          </button>
        </div>
      ) : filteredRecommendations.length === 0 ? (
        <div style={styles.emptyContainer}>
          <p>{t('No recommendations found. Select recommendations from AI Analysis in the Maturity Scorecard.')}</p>
        </div>
      ) : (
        <div style={styles.roadmapContainer}>
          {filteredRecommendations.map((doc, docIndex) => (
            <div key={doc._id} style={styles.categorySection}>
              <h2 style={styles.categoryTitle}>{doc.category}</h2>
              <div style={styles.recommendationsTable}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>{t('Recommendation')}</th>
                      <th style={styles.tableHeader}>{t('Status')}</th>
                      <th style={styles.tableHeader}>{t('Priority')}</th>
                      <th style={styles.tableHeader}>{t('Assigned To')}</th>
                      <th style={styles.tableHeader}>{t('Target Date')}</th>
                      <th style={styles.tableHeader}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.recommendations
                      .filter(rec => {
                        if (filters.status !== 'all' && rec.status !== filters.status) {
                          return false;
                        }
                        if (filters.priority !== 'all' && rec.priority !== filters.priority) {
                          return false;
                        }
                        return true;
                      })
                      .map((rec, recIndex) => (
                        <tr key={recIndex} style={styles.tableRow}>
                          <td style={styles.tableCell}>{rec.text}</td>
                          <td style={styles.tableCell}>
                            <div style={{
                              ...styles.statusBadge,
                              backgroundColor: getStatusColor(rec.status),
                            }}>
                              {rec.status}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={{
                              ...styles.priorityBadge,
                              backgroundColor: getPriorityColor(rec.priority),
                            }}>
                              {rec.priority}
                            </div>
                          </td>
                          <td style={styles.tableCell}>{rec.assignedTo || '-'}</td>
                          <td style={styles.tableCell}>{rec.targetDate || '-'}</td>
                          <td style={styles.tableCell}>
                            {editingItem && editingItem.docId === doc._id && editingItem.index === recIndex ? (
                              <div style={styles.editingControls}>
                                <button 
                                  style={styles.saveButton}
                                  onClick={() => {
                                    const form = document.getElementById(`edit-form-${doc._id}-${recIndex}`);
                                    const formData = new FormData(form);
                                    const updates = {
                                      status: formData.get('status'),
                                      priority: formData.get('priority'),
                                      assignedTo: formData.get('assignedTo'),
                                      targetDate: formData.get('targetDate'),
                                      notes: formData.get('notes')
                                    };
                                    handleUpdateRecommendation(doc._id, recIndex, updates);
                                  }}
                                >
                                  <FaSave />
                                </button>
                                <button 
                                  style={styles.cancelButton}
                                  onClick={handleCancelEdit}
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <button 
                                style={styles.editButton}
                                onClick={() => handleEditClick(doc._id, recIndex)}
                              >
                                <FaEdit />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              {/* Edit Form (Hidden until edit button is clicked) */}
              {doc.recommendations.map((rec, recIndex) => (
                editingItem && editingItem.docId === doc._id && editingItem.index === recIndex && (
                  <div key={`edit-${recIndex}`} style={styles.editFormContainer}>
                    <h3 style={styles.editFormTitle}>{t('Edit Recommendation')}</h3>
                    <form id={`edit-form-${doc._id}-${recIndex}`} style={styles.editForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>{t('Status')}:</label>
                        <select name="status" defaultValue={rec.status} style={styles.formSelect}>
                          <option value="Not Started">{t('Not Started')}</option>
                          <option value="In Progress">{t('In Progress')}</option>
                          <option value="Completed">{t('Completed')}</option>
                        </select>
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>{t('Priority')}:</label>
                        <select name="priority" defaultValue={rec.priority} style={styles.formSelect}>
                          <option value="Critical">{t('Critical')}</option>
                          <option value="High">{t('High')}</option>
                          <option value="Medium">{t('Medium')}</option>
                          <option value="Low">{t('Low')}</option>
                        </select>
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>{t('Assigned To')}:</label>
                        <input 
                          type="text" 
                          name="assignedTo" 
                          defaultValue={rec.assignedTo} 
                          placeholder={t('Enter name')} 
                          style={styles.formInput}
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>{t('Target Date')}:</label>
                        <input 
                          type="date" 
                          name="targetDate" 
                          defaultValue={rec.targetDate} 
                          style={styles.formInput}
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>{t('Notes')}:</label>
                        <textarea 
                          name="notes" 
                          defaultValue={rec.notes} 
                          placeholder={t('Add implementation notes')} 
                          style={styles.formTextarea}
                        />
                      </div>
                    </form>
                  </div>
                )
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#243444',
    margin: 0
  },
  exportButtonsContainer: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  description: {
    color: '#4C5454',
    marginBottom: '20px'
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  filterLabel: {
    fontWeight: 'bold',
    color: '#243444'
  },
  filterSelect: {
    padding: '6px 10px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    backgroundColor: '#FFFAFA'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#4C5454'
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    marginBottom: '10px',
    fontSize: '2rem',
    color: '#243444'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#7C1227'
  },
  errorIcon: {
    marginBottom: '10px',
    fontSize: '2rem'
  },
  retryButton: {
    marginTop: '15px',
    padding: '8px 16px',
    backgroundColor: '#243444',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  emptyContainer: {
    textAlign: 'center',
    padding: '40px',
    color: '#4C5454',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px'
  },
  roadmapContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  categorySection: {
    backgroundColor: '#FFFAFA',
    borderRadius: '5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  categoryTitle: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    padding: '15px 20px',
    margin: 0,
    fontSize: '18px'
  },
  recommendationsTable: {
    padding: '0',
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    padding: '12px 15px',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6',
    color: '#243444',
    fontWeight: 'bold'
  },
  tableRow: {
    borderBottom: '1px solid #e9ecef'
  },
  tableCell: {
    padding: '12px 15px',
    verticalAlign: 'middle'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#FFFAFA',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  priorityBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#FFFAFA',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  editButton: {
    backgroundColor: '#D2BA7B',
    color: '#243444',
    border: 'none',
    borderRadius: '4px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  editingControls: {
    display: 'flex',
    gap: '5px'
  },
  saveButton: {
    backgroundColor: '#198754',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  cancelButton: {
    backgroundColor: '#7C1227',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  editFormContainer: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #dee2e6'
  },
  editFormTitle: {
    fontSize: '16px',
    color: '#243444',
    marginBottom: '15px'
  },
  editForm: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '15px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  formLabel: {
    fontWeight: 'bold',
    color: '#243444',
    fontSize: '14px'
  },
  formSelect: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    backgroundColor: '#FFFAFA'
  },
  formInput: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    backgroundColor: '#FFFAFA'
  },
  formTextarea: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    backgroundColor: '#FFFAFA',
    minHeight: '100px',
    resize: 'vertical'
  }
};

export default ImplementationRoadmap;
